import React from "react";
import { Check, User } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import HelpChat from "./HelpChat";

// Presentation-only vertical stepper rail. Reads the same `steps` array the
// old horizontal header consumed — status is still owned by the parent.
const StepProgressSidebar = ({
  steps = [],
  candidateName,
  interviewId,
  eyebrow = "CLEARANCE",
  title = "Pre-interview check",
  // Drops the secondary-camera chip from the help chat when the round
  // doesn't ask for a phone.
  showSecondaryCameraHelp = false,
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  // Node icon: green check once a step is finished. `autoComplete` steps
  // (nothing to do on them) show the check the moment they're reached.
  const isDone = (step) =>
    step.status === "completed" || (step.status === "active" && step.autoComplete);

  const initials = (candidateName || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <aside
      className={`hidden md:flex w-[330px] shrink-0 flex-col px-5 pt-7 pb-6 border-r ${
        isLight
          ? "bg-[#FAFAFB] border-[#EFEFF3]"
          : "bg-gray-900/60 border-gray-700/50 backdrop-blur-sm"
      }`}
    >
      {/* Candidate identity */}
      <div className="flex items-center gap-2.5">
        <div className="w-12 h-12 rounded-full shrink-0 p-[1.5px] bg-gradient-to-br from-[#2F64FF] to-[#FB54AE]">
          <div
            className={`w-full h-full rounded-full flex items-center justify-center text-[14px] font-semibold ${
              isLight ? "bg-white text-gray-600" : "bg-gray-900 text-gray-200"
            }`}
          >
            {initials || <User className="w-[15px] h-[15px]" strokeWidth={1.75} />}
          </div>
        </div>
        <div className="min-w-0">
          <p
            className={`text-[17px] font-normal leading-[1.3] truncate ${
              isLight ? "text-slate-900" : "text-white"
            }`}
            title={candidateName || "Candidate"}
          >
            {candidateName || "Candidate"}
          </p>
          {interviewId && (
            <p
              className={`text-[12px] leading-[1.3] mt-px truncate ${
                isLight ? "text-slate-500" : "text-gray-400"
              }`}
              title={interviewId}
            >
              Interview ID: {interviewId}
            </p>
          )}
        </div>
      </div>

      <div className="self-center max-w-full">
      {/* Section heading */}
      <div className="mt-11">
        <p
          className={`text-[12px] font-medium tracking-[0.16em] leading-none mb-[5px] ${
            isLight ? "text-slate-400" : "text-gray-400"
          }`}
        >
          {eyebrow}
        </p>
        <h2
          className={`text-left text-[17px] font-medium leading-snug ${
            isLight ? "text-slate-900" : "text-white"
          }`}
        >
          {title}
        </h2>
      </div>

      {/* Step rail — 24px node + 36px trailing gap = 60px rows */}
      <ol className="flex flex-col mt-14">
        {steps.map((step, index) => {
          const done = isDone(step);
          const isActive = step.status === "active";
          const isLast = index === steps.length - 1;

          return (
            <li key={step.id} className="flex items-start gap-3">
              <div className="pt-[14px] flex flex-col items-center self-stretch">
                <div
                  className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[11px] font-medium ${
                    done
                      ? "bg-[#22C55E] text-white"
                      : isActive
                      ? isLight
                        ? "bg-white border-[1.5px] border-slate-800 text-slate-800"
                        : "bg-transparent border-[1.5px] border-white text-white"
                      : isLight
                      ? "bg-white border border-[#E6E7EC] text-slate-400"
                      : "bg-gray-700/60 border border-gray-600 text-gray-400"
                  }`}
                >
                  {done ? (
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  ) : (
                    index + 1
                  )}
                </div>

                {/* Green up to where you've finished, then a darker segment
                    trailing the step you're on, then plain grey. */}
                {!isLast && (
                  <div
                    className={`w-px flex-1 my-1.5 ${
                      done
                        ? "bg-[#22C55E]"
                        : isActive
                        ? isLight
                          ? "bg-[#CBD5E1]"
                          : "bg-gray-500"
                        : isLight
                        ? "bg-[#E6E7EC]"
                        : "bg-gray-700"
                    }`}
                  />
                )}
              </div>

              <span
                className={`pt-[16px] text-[15px] leading-tight ${
                  isLast ? "" : "pb-9"
                } ${
                  isActive
                    ? `font-medium ${isLight ? "text-slate-900" : "text-white"}`
                    : isLight
                    ? "text-slate-400"
                    : "text-gray-500"
                }`}
              >
                {step.title}
              </span>
            </li>
          );
        })}
      </ol>
      </div>

      {/* Sits at the foot of the rail; opens a chat panel over the main pane */}
      <HelpChat showSecondaryCamera={showSecondaryCameraHelp} />
    </aside>
  );
};

export default StepProgressSidebar;
